// Deterministic scripted bot. Used by the tests and by scripts/validate-week.ts
// to estimate how survivable a generated track is. Not part of gameplay.

import {
  HITBOX_SLIDE_W,
  HITBOX_STAND_W,
  JUMP_LAST_AIR_FRAME,
  MAX_FRAMES,
  MAX_INPUT_EVENTS,
  PLAYER_START_X,
  SLIDE_MAX_FRAMES,
  SPIKE_CLEAR_FIRST,
  SPIKE_CLEAR_LAST,
} from './constants';
import { frameAtDistance } from './distance';
import { mulberry32, nextInt, type Rng } from './prng';
import { simulateRun } from './run';
import { Sim } from './sim';
import { generateTrack } from './track';
import { Action, type InputEvent, type Obstacle, type RunResult } from './types';

export interface BotConfig {
  /** Frames added to every ideal action frame. Positive = reacts late. */
  reaction?: number;
  /** Chance in permille (0..1000) that the bot botches a given obstacle. */
  errorPermille?: number;
  /** Seed for the error rolls; independent of the track seed. */
  seed?: number;
  /** Emit an explicit SlideEnd after each bar (otherwise the 24-frame timer ends it). */
  explicitSlideEnd?: boolean;
}

// Collisions are evaluated after the frame's movement, so during frame f the
// player sits at PLAYER_START_X + distanceAtFrame(f + 1) — hence the `- 1`.

/** First frame during which the player's collision x is >= v. */
function firstFrameAtX(v: number): number {
  return frameAtDistance((v - PLAYER_START_X) | 0) - 1;
}
/** First frame during which the player's collision x is > v. */
function firstFrameAfterX(v: number): number {
  return frameAtDistance((v - PLAYER_START_X + 1) | 0) - 1;
}

/**
 * Frames of margin the bot leaves on either side of a bar's slide window.
 * Both margins model *anticipation*: humans see a bar coming and press early —
 * sliding early is free (the 24-frame timer still covers the window). The
 * original last-moment margins (lead 3, release 6) made late bars a binary
 * reaction check: at max speed any bot with reaction ≥ 4 slid one frame late
 * and died 100% of the time — playtests showed "late bars kill 12–15% of
 * reachers", a bot artifact, not track design. Lead 10 still satisfies
 * `start ≥ fe − 23` for every bar window (span ≤ 13 frames).
 */
const SLIDE_LEAD_FRAMES = 10;
const SLIDE_RELEASE_FRAMES = 14;

function clamp(v: number, lo: number, hi: number): number {
  if (hi < lo) return lo;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

/** The frame window during which an obstacle overlaps the player horizontally. */
function overlapWindow(o: Obstacle, hitW: number): { fs: number; fe: number } {
  const fs = firstFrameAfterX((o.x - hitW) | 0);
  const fe = firstFrameAtX((o.x + o.w) | 0) - 1;
  return { fs, fe };
}

/** Ideal take-off / slide frame for one obstacle, before reaction & error noise. */
function idealAction(o: Obstacle): { frame: number; action: Action; endFrame: number } | null {
  if (o.type === 'spike') {
    const { fs, fe } = overlapWindow(o, HITBOX_STAND_W);
    const lo = fe - SPIKE_CLEAR_LAST;
    const hi = fs - SPIKE_CLEAR_FIRST;
    return { frame: hi < lo ? hi : (lo + hi) >> 1, action: Action.Jump, endFrame: -1 };
  }
  if (o.type === 'gap') {
    // The player is unsupported while the whole foot span is inside the gap.
    const fs = firstFrameAtX(o.x);
    const fe = firstFrameAfterX((o.x + o.w - HITBOX_STAND_W) | 0) - 1;
    const lo = fe - JUMP_LAST_AIR_FRAME;
    const hi = fs;
    return { frame: hi < lo ? hi : (lo + hi) >> 1, action: Action.Jump, endFrame: -1 };
  }
  // bar: slide across the whole overlap window.
  const { fs, fe } = overlapWindow(o, HITBOX_SLIDE_W);
  const lo = fe - (SLIDE_MAX_FRAMES - 1);
  const start = clamp(fs - SLIDE_LEAD_FRAMES, lo, fs);
  return { frame: start, action: Action.SlideStart, endFrame: fe + SLIDE_RELEASE_FRAMES };
}

/**
 * Builds an input stream for a track. Deterministic in (obstacles, cfg).
 * `reaction` shifts every action; `errorPermille` makes the bot occasionally
 * skip an obstacle entirely or mistime it badly.
 */
export function botInputs(obstacles: readonly Obstacle[], cfg: BotConfig = {}): InputEvent[] {
  const reaction = cfg.reaction ?? 0;
  const errorPermille = cfg.errorPermille ?? 0;
  const rng: Rng = mulberry32(cfg.seed ?? 1);
  const explicitSlideEnd = cfg.explicitSlideEnd ?? true;

  const events: InputEvent[] = [];

  for (let i = 0; i < obstacles.length; i++) {
    const plan = idealAction(obstacles[i]!);
    if (plan === null) continue;

    let offset = reaction;
    if (errorPermille > 0 && nextInt(rng, 1000) < errorPermille) {
      const mode = nextInt(rng, 3);
      if (mode === 0) continue; // whiffed the input entirely
      offset += (mode === 1 ? 1 : -1) * (5 + nextInt(rng, 11));
    }

    const f = plan.frame + offset;
    if (f < 0 || f >= MAX_FRAMES) continue;
    events.push({ f, a: plan.action });

    if (plan.action === Action.SlideStart && explicitSlideEnd) {
      const end = plan.endFrame + offset;
      if (end > f && end < MAX_FRAMES) events.push({ f: end, a: Action.SlideEnd });
    }
  }

  // Stable sort keeps per-obstacle ordering (SlideStart before its SlideEnd, and
  // earlier obstacles first) when two actions land on the same frame.
  events.sort((a, b) => a.f - b.f);

  // Respect the wire limits: at most 4 events per frame, 600 total.
  const out: InputEvent[] = [];
  let lastFrame = -1;
  let perFrame = 0;
  for (let i = 0; i < events.length && out.length < MAX_INPUT_EVENTS; i++) {
    const e = events[i]!;
    if (e.f === lastFrame) {
      if (perFrame >= 4) continue;
      perFrame++;
    } else {
      lastFrame = e.f;
      perFrame = 1;
    }
    out.push(e);
  }
  return out;
}

/**
 * Drops the planned events the player would never have produced because the run
 * already ended — a real client stops sending input at the death frame.
 */
export function truncateAtDeath(seed: number, obstacles: Obstacle[], planned: InputEvent[]): InputEvent[] {
  const sim = new Sim(seed, obstacles);
  const used: InputEvent[] = [];
  let i = 0;
  for (let f = 0; f < MAX_FRAMES; f++) {
    const start = i;
    while (i < planned.length && planned[i]!.f === f) i++;
    const evs = start === i ? [] : planned.slice(start, i);
    for (let k = 0; k < evs.length; k++) used.push(evs[k]!);
    sim.step(evs);
    if (sim.state.dead) break;
    if (sim.state.finished) break;
  }
  return used;
}

export interface BotRun {
  inputs: InputEvent[];
  result: RunResult;
  obstacles: Obstacle[];
}

/**
 * Generates the track for `seed`, plays it with the bot, and verifies the run.
 * `gen` = the track's persisted sim_version (§4bis.24); defaults to current.
 */
export function runBot(seed: number, cfg: BotConfig = {}, gen?: string): BotRun {
  const obstacles = generateTrack(seed, gen);
  const inputs = truncateAtDeath(seed, obstacles, botInputs(obstacles, cfg));
  return { inputs, result: simulateRun(seed, inputs, gen), obstacles };
}
